import type { TextareaHTMLAttributes } from "react";
import {
  useController,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import FormFieldShell from "./FormFieldShell";
import { cn } from "@/lib/utils";

export interface TextareaInputProps<TFieldValues extends FieldValues>
  extends Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    "name" | "defaultValue"
  > {
  control: Control<TFieldValues>;
  label?: string;
  name: Path<TFieldValues>;
  required?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  lineHeight?: string | number;
}

export function TextareaInput<TFieldValues extends FieldValues>({
  className,
  control,
  id,
  label,
  labelClassName,
  errorClassName,
  name,
  required,
  wrapperClassName = "w-full",
  lineHeight = "1.5",
  ...inputProps
}: Readonly<TextareaInputProps<TFieldValues>>) {
  const { field, fieldState, formState } = useController({
    control,
    name,
  });

  const inputId = id ?? name;
  const shouldShowError =
    !!(fieldState.isTouched || formState.isSubmitted) && !!fieldState.error;

  return (
    <FormFieldShell
      error={shouldShowError ? fieldState.error?.message : undefined}
      htmlFor={inputId}
      label={label}
      labelClassName={labelClassName}
      errorClassName={errorClassName}
      required={required}
      wrapperClassName={wrapperClassName}
    >
      <textarea
        {...inputProps}
        {...field}
        className={cn(
          "w-full p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:focus:ring-purple-400 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none leading-relaxed disabled:opacity-50 disabled:cursor-not-allowed",
          shouldShowError && "border-red-500 dark:border-red-500 focus:ring-red-500 focus:border-red-500",
          className
        )}
        id={inputId}
        value={field.value ?? ""}
        style={{ lineHeight: Number(lineHeight), ...inputProps.style }}
      />
    </FormFieldShell>
  );
}

export default TextareaInput;
